import { useUser } from '@clerk/clerk-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { useApi, type MeResponse, type DashboardResponse } from '../lib/api';

// Mirror of the row shape returned by GET /admin/clients.
type ClientRow = {
  id: string;
  name: string;
  contact_email: string;
  plan_tier: 'standard' | 'premium' | null;
  stripe_status: string | null;
  has_linked_user: 0 | 1;
  has_active_extension_token: 0 | 1;
  last_lead_at: number | null;
  total_leads: number;
  buyer_intent_leads: number;
  new_leads: number;
  won_leads: number;
  leads_last_7d: number;
};

export function Dashboard() {
  const { user, isLoaded: clerkLoaded } = useUser();
  const { request } = useApi();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  // Per-client roll-up — owner-only view of every client and their lead stats.
  const [clientRows, setClientRows] = useState<ClientRow[] | null>(null);
  const [clientsErr, setClientsErr] = useState<string | null>(null);

  // Birds-eye dashboard data — 7-day trend + top communities. Loaded for
  // every signed-in user (scope-filtered server-side).
  const [dash, setDash] = useState<DashboardResponse | null>(null);
  const [dashErr, setDashErr] = useState<string | null>(null);

  async function refreshMe() {
    setLoading(true);
    setError(null);
    try {
      const data = await request<MeResponse>('/api/me');
      setMe(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function loadClientRows() {
    setClientsErr(null);
    try {
      const res = await request<{ clients: ClientRow[] }>('/admin/clients');
      setClientRows(res.clients);
    } catch (e) {
      setClientsErr((e as Error).message);
    }
  }

  async function loadDashboard() {
    setDashErr(null);
    try {
      const res = await request<DashboardResponse>('/api/me/dashboard');
      setDash(res);
    } catch (e) {
      setDashErr((e as Error).message);
    }
  }

  useEffect(() => {
    if (clerkLoaded) refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkLoaded]);

  // Pull birds-eye dashboard data once we know the user is linked.
  useEffect(() => {
    if (me && !me.user_not_linked) {
      void loadDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Pull per-client roll-up for owner OR va_admin — admin-VAs see the whole
  // agency rollup just like the owner.
  useEffect(() => {
    if (
      me &&
      !me.user_not_linked &&
      (me.user.role === 'owner' || me.user.role === 'va_admin')
    ) {
      void loadClientRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Top-of-page agency totals are the SUM across all clients (owners only).
  // For non-owners we just use whatever /api/me returned for them.
  const agencyTotals = useMemo(() => {
    if (!clientRows) return null;
    return clientRows.reduce(
      (acc, r) => ({
        total: acc.total + (r.total_leads ?? 0),
        buyer_intent: acc.buyer_intent + (r.buyer_intent_leads ?? 0),
        new_leads: acc.new_leads + (r.new_leads ?? 0),
        won_leads: acc.won_leads + (r.won_leads ?? 0),
        last_7d: acc.last_7d + (r.leads_last_7d ?? 0),
      }),
      { total: 0, buyer_intent: 0, new_leads: 0, won_leads: 0, last_7d: 0 },
    );
  }, [clientRows]);

  async function runSeed() {
    setSeeding(true);
    setError(null);
    try {
      await request('/admin/seed', { method: 'POST' });
      await refreshMe();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSeeding(false);
    }
  }

  const userRole = me && !me.user_not_linked ? me.user.role : undefined;

  return (
    <AppShell {...(userRole ? { userRole } : {})}>
      <main className="page">
        {!clerkLoaded || loading ? (
          <section className="card">
            <p className="muted">Loading…</p>
          </section>
        ) : error ? (
          <section className="card">
            <h1>Something went wrong</h1>
            <p className="muted">{error}</p>
            <button className="btn btn-secondary" onClick={refreshMe}>
              Retry
            </button>
          </section>
        ) : me && me.user_not_linked ? (
          <section className="card">
            <h1>Welcome, {me.clerk.email ?? user?.id}</h1>
            <p className="muted">
              Your Clerk account isn't linked to a GroupPulse user yet. {me.clerk.is_owner_email
                ? 'Click below to bootstrap yourself as the agency owner.'
                : 'Ask the agency owner to invite you.'}
            </p>
            {me.clerk.is_owner_email && (
              <div className="actions" style={{ justifyContent: 'flex-start' }}>
                <button className="btn btn-primary" onClick={runSeed} disabled={seeding}>
                  {seeding ? 'Setting up…' : 'Run setup'}
                </button>
              </div>
            )}
          </section>
        ) : me && !me.user_not_linked ? (
          <>
            {/* 8-card stats grid at the top — icons + colored backgrounds,
                same visual rhythm as the extension's v2.10.3 dashboard.
                For owners these are summed across all clients; for everyone
                else they're scoped to their own access. */}
            <DashStatsGrid me={me} dash={dash} agencyTotals={agencyTotals} />

            {/* Two-up: AI analysis | 7-Day Lead Trend (total + buyer-intent
                stacked, matches the extension). The AI card is a placeholder
                until the agency-wide strategic-insights endpoint lands. */}
            {dashErr ? (
              <section className="card">
                <p style={{ color: '#b91c1c', margin: 0 }}>
                  Couldn&apos;t load dashboard stats: {dashErr}
                </p>
              </section>
            ) : !dash ? (
              <section className="card">
                <p className="muted" style={{ margin: 0 }}>Loading insights…</p>
              </section>
            ) : (
              <>
                <div className="dash-two-up">
                  <AiAnalysisCard />
                  <TrendChartCard trend={dash.trend} />
                </div>

                {/* Top communities — where leads are coming from, by volume.
                    Replaces the old horizontal-bar list with a clean table
                    so the user can scan and click into a Leads view
                    filtered by the group/subreddit they care about. */}
                <TopCommunitiesCard communities={dash.communities} />
              </>
            )}

            {/* Owner-only: per-client breakdown. The bird's-eye view of the
                whole book of business in one table. */}
            {me.user.role === 'owner' && (
              <section className="card">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <h2 style={{ margin: 0 }}>Clients At A Glance</h2>
                  <Link
                    to="/admin/clients"
                    style={{ fontSize: 14 }}
                  >
                    Full client list →
                  </Link>
                </div>
                <p className="muted" style={{ marginTop: 0 }}>
                  Lead counts per client. Click a name to drill into their
                  profile, keywords, and lead history.
                </p>

                {clientsErr ? (
                  <p style={{ color: '#b91c1c' }}>{clientsErr}</p>
                ) : !clientRows ? (
                  <p className="muted">Loading clients…</p>
                ) : clientRows.length === 0 ? (
                  <p className="muted" style={{ fontStyle: 'italic' }}>
                    No clients yet.{' '}
                    <Link to="/admin/new-client">
                      Generate a checkout link
                    </Link>{' '}
                    to onboard your first one.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table className="overview-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>Client</th>
                          <th>Last 7d</th>
                          <th>Total</th>
                          <th>Buyer intent</th>
                          <th>New</th>
                          <th>Won</th>
                          <th>Last activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientRows.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <Link
                                to={`/admin/clients/${c.id}`}
                                style={{
                                  fontWeight: 500,
                                  color: 'var(--text)',
                                }}
                              >
                                {c.name}
                              </Link>
                              <div
                                className="muted"
                                style={{ fontSize: 12, marginTop: 2 }}
                              >
                                {c.contact_email}
                                {c.plan_tier && (
                                  <>
                                    {' · '}
                                    <span style={{ textTransform: 'capitalize' }}>
                                      {c.plan_tier}
                                    </span>
                                  </>
                                )}
                                {c.stripe_status && c.stripe_status !== 'active' && (
                                  <>
                                    {' · '}
                                    <span
                                      style={{
                                        color: '#b91c1c',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {c.stripe_status}
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="num">
                              <strong>{c.leads_last_7d.toLocaleString()}</strong>
                            </td>
                            <td className="num">{c.total_leads.toLocaleString()}</td>
                            <td className="num">
                              {c.buyer_intent_leads > 0 ? (
                                <strong style={{ color: '#15803d' }}>
                                  {c.buyer_intent_leads.toLocaleString()}
                                </strong>
                              ) : (
                                <span className="muted">0</span>
                              )}
                            </td>
                            <td className="num">{c.new_leads.toLocaleString()}</td>
                            <td className="num">{c.won_leads.toLocaleString()}</td>
                            <td className="muted" style={{ fontSize: 13 }}>
                              {formatRelative(c.last_lead_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            <section className="card">
              <h2 style={{ marginTop: 0 }}>What&apos;s Next</h2>
              <p style={{ marginBottom: me.user.role === 'owner' ? undefined : 0 }}>
                Open the <Link to="/leads">Leads</Link> page to see what
                GroupPulse has captured for you.
              </p>
              {me.user.role === 'owner' && (
                <p style={{ marginBottom: 0 }}>
                  Onboarding a new client?{' '}
                  <Link to="/admin/new-client">Generate a checkout link</Link>{' '}
                  and email it to them.
                </p>
              )}
            </section>
          </>
        ) : null}
      </main>
    </AppShell>
  );
}

// -----------------------------------------------------------------------------
// DashStatsGrid — 8-card stats band lifted from the extension's v2.10.3
// dashboard. Same icon vocabulary, colored backgrounds, hover-to-link.
// Owners see agency-wide totals (summed across all clients); everyone else
// sees their own scoped counts.
// -----------------------------------------------------------------------------
type AgencyTotals = {
  total: number;
  buyer_intent: number;
  new_leads: number;
  won_leads: number;
  last_7d: number;
};

function DashStatsGrid({
  me,
  dash,
  agencyTotals,
}: {
  me: Extract<MeResponse, { user_not_linked: false }>;
  dash: DashboardResponse | null;
  agencyTotals: AgencyTotals | null;
}) {
  const total = agencyTotals?.total ?? me.counts.total;
  const buyer = agencyTotals?.buyer_intent ?? me.counts.buyer_intent;
  const newL = agencyTotals?.new_leads ?? me.counts.new_leads;
  const won = agencyTotals?.won_leads ?? me.counts.won_leads;
  // dash.headline carries the additional metrics (analyzed, last_7d). For
  // owners, agencyTotals.last_7d is already the agency sum; fall back to
  // dash.headline otherwise.
  const last7 = agencyTotals?.last_7d ?? dash?.headline.last_7d ?? 0;
  const analyzed = dash?.headline.analyzed ?? 0;
  // Communities-derived stats so the user sees "where" their leads cluster
  // alongside the raw counts.
  const activeCommunities =
    dash?.communities.filter((c) => c.lead_count > 0).length ?? 0;
  const topCommunity = dash?.communities[0] ?? null;

  const pctBuyer = total > 0 ? Math.round((buyer / total) * 100) : 0;
  const pctAnalyzed = total > 0 ? Math.round((analyzed / total) * 100) : 0;

  const tiles: {
    label: string;
    value: string;
    sub: string;
    iconClass: string;
    icon: React.ReactNode;
    href: string;
  }[] = [
    {
      label: 'Total leads',
      value: total.toLocaleString(),
      sub: `${last7.toLocaleString()} this week`,
      iconClass: 'blue',
      icon: <IconBarChart />,
      href: '/leads',
    },
    {
      label: 'Buyer intent',
      value: buyer.toLocaleString(),
      sub: `${pctBuyer}% of total`,
      iconClass: 'green',
      icon: <IconTarget />,
      href: '/leads?intent=buyer_intent',
    },
    {
      label: 'Won leads',
      value: won.toLocaleString(),
      sub: 'marked as won',
      iconClass: 'green',
      icon: <IconCheck />,
      href: '/leads?status=won',
    },
    {
      label: 'New',
      value: newL.toLocaleString(),
      sub: 'leads waiting on you',
      iconClass: 'purple',
      icon: <IconInbox />,
      href: '/leads?status=new',
    },
    {
      label: 'Last 7 days',
      value: last7.toLocaleString(),
      sub: 'new leads',
      iconClass: 'gray',
      icon: <IconCalendar />,
      href: '/leads?time=7d',
    },
    {
      label: 'Analyzed by AI',
      value: analyzed.toLocaleString(),
      sub: `${pctAnalyzed}% of total`,
      iconClass: 'purple',
      icon: <IconSparkles />,
      href: '/leads',
    },
    {
      label: 'Top community',
      value: topCommunity ? topCommunity.lead_count.toLocaleString() : '0',
      sub: topCommunity
        ? topCommunity.group_name.slice(0, 28)
        : 'no captures yet',
      iconClass: 'yellow',
      icon: <IconTrophy />,
      href: '/leads',
    },
    {
      label: 'Active communities',
      value: activeCommunities.toLocaleString(),
      sub: 'with captures',
      iconClass: 'red',
      icon: <IconUsers />,
      href: '/leads',
    },
  ];

  return (
    <div className="dash-stats-grid">
      {tiles.map((t) => (
        <Link key={t.label} to={t.href} className="dash-stat-card">
          <div className={`dash-stat-icon ${t.iconClass}`}>{t.icon}</div>
          <div className="dash-stat-content">
            <div className="dash-stat-label">{t.label}</div>
            <div className="dash-stat-value">{t.value}</div>
            <div className="dash-stat-sub">{t.sub}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AiAnalysisCard — placeholder for the agency-wide strategic-insights
// endpoint. The extension's local dashboard has the same card backed by
// /api/ai/insights; cloud rollout pending. Refresh just shows a friendly
// "not yet available" message for now so the card layout is present and
// the wire-up is a one-line backend swap when ready.
// -----------------------------------------------------------------------------
function AiAnalysisCard() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'unavailable'>(
    'idle',
  );

  return (
    <section className="card">
      <div className="dash-card-header">
        <div>
          <h2>
            <span className="ai-sparkle">✦</span> AI analysis
          </h2>
          <div className="dash-card-subtitle">
            Strategic recommendations · click any to see related leads
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => {
            setStatus('loading');
            // Two beats of "loading…" so the click feels real, then explain
            // why we have nothing to show yet.
            setTimeout(() => setStatus('unavailable'), 600);
          }}
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div
        style={{
          padding: '36px 16px',
          textAlign: 'center',
          color: 'var(--muted)',
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {status === 'unavailable'
          ? 'Agency-wide AI insights are coming next. For per-client insights, open a client and use Generate Insights there.'
          : status === 'loading'
            ? 'Refreshing…'
            : 'Click Refresh to generate AI strategic insights from your captured leads.'}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// TrendChartCard — 7-day stacked bars colored by PLATFORM: Facebook (blue)
// on the bottom, Reddit (orange) stacked on top. /api/me/dashboard already
// returns d.facebook + d.reddit per day, so this is purely a render change.
// The extension's renderTrendChart in dashboard.js renders the same shape.
// -----------------------------------------------------------------------------
function TrendChartCard({ trend }: { trend: DashboardResponse['trend'] }) {
  const max = Math.max(1, ...trend.map((d) => d.total));
  return (
    <section className="card">
      <div className="dash-card-header">
        <div>
          <h2>7-day lead trend</h2>
          <div className="dash-card-subtitle">
            Leads captured per day —{' '}
            <span style={{ color: '#1877F2', fontWeight: 600 }}>Facebook</span>{' '}
            and{' '}
            <span style={{ color: '#FF4500', fontWeight: 600 }}>Reddit</span>{' '}
            stacked.
          </div>
        </div>
      </div>
      {trend.every((d) => d.total === 0) ? (
        <div className="dash-empty">
          No leads captured in the last 7 days. Run a sweep from the extension
          popup.
        </div>
      ) : (
        <>
          <div className="dash-trend-bars">
            {trend.map((d) => {
              const fbPct = (d.facebook / max) * 100;
              const rdPct = (d.reddit / max) * 100;
              const label = new Date(
                d.date + 'T00:00:00',
              ).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              });
              return (
                <div
                  key={d.date}
                  className="dash-trend-day"
                  title={`${label}: ${d.total} lead${d.total === 1 ? '' : 's'} (${d.facebook} Facebook · ${d.reddit} Reddit)`}
                >
                  <div className="dash-trend-bar-stack">
                    {d.total > 0 && (
                      <>
                        {/* Facebook segment — anchored to the bottom by the
                            parent's flex-direction: column-reverse. */}
                        <div
                          className="dash-trend-bar-fb"
                          style={{ height: `${fbPct}%` }}
                        >
                          {d.facebook > 0 && (
                            <span className="dash-trend-bar-num">
                              {d.facebook}
                            </span>
                          )}
                        </div>
                        {/* Reddit segment — stacked on top. */}
                        <div
                          className="dash-trend-bar-rd"
                          style={{ height: `${rdPct}%` }}
                        >
                          {d.reddit > 0 && (
                            <span className="dash-trend-bar-num">{d.reddit}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="dash-trend-day-label">{label}</div>
                </div>
              );
            })}
          </div>
          <div className="dash-trend-legend">
            <span>
              <span className="dash-trend-legend-dot legend-fb" />
              Facebook
            </span>
            <span>
              <span className="dash-trend-legend-dot legend-rd" />
              Reddit
            </span>
          </div>
        </>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// TopCommunitiesCard — clean table of the groups/subreddits producing the
// most leads. Click a row to jump to a filtered Leads view. Replaces the
// old horizontal-bar variant with the table layout the user prefers.
// -----------------------------------------------------------------------------
function TopCommunitiesCard({
  communities,
}: {
  communities: DashboardResponse['communities'];
}) {
  return (
    <section className="card">
      <div className="dash-card-header">
        <div>
          <h2>Top communities</h2>
          <div className="dash-card-subtitle">
            All watched groups by lead count · click any row to filter Leads
          </div>
        </div>
      </div>
      {communities.length === 0 ? (
        <div className="dash-empty">
          No leads captured yet — run a sweep to see group performance.
        </div>
      ) : (
        <table className="dash-table">
          <thead>
            <tr>
              <th>Group</th>
              <th style={{ textAlign: 'right' }}>Leads</th>
              <th style={{ textAlign: 'right' }}>Buyer intent</th>
            </tr>
          </thead>
          <tbody>
            {communities.map((c) => {
              const key = `${c.platform ?? 'unknown'}:${c.group_name}`;
              const href = `/leads?group=${encodeURIComponent(c.group_name)}`;
              return (
                <tr
                  key={key}
                  className="clickable"
                  onClick={() => (window.location.href = href)}
                >
                  <td>
                    <strong>{c.group_name}</strong>
                    {c.platform && (
                      <span
                        className="muted"
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          textTransform: 'capitalize',
                        }}
                      >
                        {c.platform}
                      </span>
                    )}
                  </td>
                  <td className="num">
                    <strong>{c.lead_count.toLocaleString()}</strong>
                  </td>
                  <td className="num">
                    {c.buyer_intent_count > 0 ? (
                      <strong style={{ color: '#15803d' }}>
                        {c.buyer_intent_count.toLocaleString()}
                      </strong>
                    ) : (
                      <span className="muted">0</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Inline icons for the stat grid — tiny stroke-only SVGs so they pick up
// the wrapping .dash-stat-icon's currentColor and stay crisp at 18px.
// -----------------------------------------------------------------------------
function IconBarChart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}
function IconTarget() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconInbox() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconSparkles() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v3M12 18v3M5 12H2M22 12h-3M6.34 6.34l-2.12-2.12M19.78 19.78l-2.12-2.12M6.34 17.66l-2.12 2.12M19.78 4.22l-2.12 2.12" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconTrophy() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 1 1-10 0V4z" />
      <path d="M17 6h2a2 2 0 0 1 2 2v0a4 4 0 0 1-4 4M7 6H5a2 2 0 0 0-2 2v0a4 4 0 0 0 4 4" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function formatRelative(ms: number | null): string {
  if (!ms) return 'never';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 30 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(ms).toLocaleDateString();
}

